import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import Buscador from '../../components/Formularios/Buscador/Buscador';
import Cargaimg from '../../components/Formularios/Cargarimg/Cargaimg';
import { API_URL, buildBackendImgUrl } from '../../utils/api';
import { Link } from 'react-router-dom';
import { Head, Hero, HeroTitle, HeroSub, HeroTags, Body, PrincipalGrid, ArticleCard, ArticleImg,
   ArticleInfo, ArticlePrice, ArticleNombre, ArticleTrastero } from './Principal.styles';

const ModalFiltro = lazy(() => import('../../components/Modal/ModalFiltro/ModalFiltro'));

const LIMIT = 20;
const FILTROS_VACIO = {
  q: '', filtro: 'trastero', orden: '',
  categoriaCodigo: '', subcategoriaId: '',
  precioMin: '', precioMax: '',
};

function contarFiltros(f) {
  return [
    f.orden, f.categoriaCodigo, f.subcategoriaId, f.precioMin, f.precioMax,
  ].filter(Boolean).length;
}

function buildQS(filtros, offset) {
  const qs = new URLSearchParams({ limit: LIMIT, offset });
  if (filtros.q)              qs.set('q', filtros.q);
  if (filtros.filtro)         qs.set('filtro', filtros.filtro);
  if (filtros.orden)          qs.set('orden', filtros.orden);
  if (filtros.categoriaCodigo) qs.set('categoria_codigo', filtros.categoriaCodigo);
  if (filtros.subcategoriaId)  qs.set('subcategoria_id', filtros.subcategoriaId);
  if (filtros.precioMin)      qs.set('precio_min', filtros.precioMin);
  if (filtros.precioMax)      qs.set('precio_max', filtros.precioMax);
  return qs;
}

function Principal() {
  const [tasks, setTasks]             = useState([]);
  const [offset, setOffset]           = useState(0);
  const [hasMore, setHasMore]         = useState(true);
  const [cargando, setCargando]       = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [filtrosActivos, setFiltrosActivos] = useState(FILTROS_VACIO);
  const [filtroAbierto, setFiltroAbierto]   = useState(false);
  const [vistaPlana, setVistaPlana]         = useState(false);
  const sentinelRef = useRef(null);
  const loadingRef  = useRef(false);

  const cargar = useCallback(async (filtros, off) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (off === 0) setCargando(true); else setCargandoMas(true);
    try {
      const res  = await fetch(`${API_URL}/api/trasteros?${buildQS(filtros, off)}`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setTasks(prev => off === 0 ? data : [...prev, ...data]);
      setHasMore(data.length === LIMIT);
      setOffset(off + data.length);
    } catch {}
    finally {
      loadingRef.current = false;
      setCargando(false);
      setCargandoMas(false);
    }
  }, []);

  useEffect(() => { cargar(FILTROS_VACIO, 0); }, [cargar]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || cargandoMas) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) cargar(filtrosActivos, offset); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, cargandoMas, offset, filtrosActivos, cargar]);

  const aplicarFiltros = (filtros) => {
    setFiltrosActivos(filtros);
    setOffset(0);
    setHasMore(true);
    cargar(filtros, 0);
  };

  const buscar = (q) => {
    const filtros = { ...filtrosActivos, q };
    setFiltrosActivos(filtros);
    setOffset(0);
    setHasMore(true);
    cargar(filtros, 0);
  };

  return (
    <div>
      <Helmet>
        <title>Trastero — Compra, vende e intercambia artículos de segunda mano</title>
        <meta name="description" content="Crea tu Trastero, Garaje, Bodega o Rastro y compra, vende o intercambia artículos de segunda mano. La plataforma gratuita para dar salida a lo que ya no usas." />
        <meta property="og:title"       content="Trastero — Compra, vende e intercambia artículos de segunda mano" />
        <meta property="og:description" content="Crea tu Trastero, Garaje, Bodega o Rastro y compra, vende o intercambia artículos de segunda mano." />
        <meta property="og:image"       content="https://trasteroweb.es/og-image.jpg" />
        <meta property="og:url"         content="https://trasteroweb.es" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="Trastero" />
        <meta name="twitter:card"       content="summary_large_image" />
        <meta name="twitter:title"      content="Trastero — Segunda mano" />
        <meta name="twitter:description" content="Compra, vende o intercambia artículos de tu trastero, garaje, bodega o rastro." />
        <meta name="twitter:image"      content="https://trasteroweb.es/og-image.jpg" />
        <link rel="canonical"           href="https://trasteroweb.es" />
      </Helmet>
      <Head>
        <Buscador
          onBuscar={buscar}
          onAbrirFiltro={(textoActual) => {
            if (textoActual) setFiltrosActivos(prev => ({ ...prev, q: textoActual }));
            setFiltroAbierto(true);
          }}
          totalFiltros={contarFiltros(filtrosActivos)}
          vistaPlana={vistaPlana}
          onToggleVista={() => setVistaPlana(v => !v)}
        />
      </Head>
      <Body>
        {cargando ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#666', fontSize: 18 }}>
            ⏳ Cargando...
          </div>
        ) : tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#666', fontSize: 16 }}>
            📦 No hay artículos publicados aún
          </div>
        ) : vistaPlana ? (
          <PrincipalGrid>
            {tasks.map(task => {
              let currentUserId = null;
              try { const s = localStorage.getItem('user'); if (s) currentUserId = JSON.parse(s).id; } catch {}
              const isOwner = currentUserId && Number(task.usuario_id) === Number(currentUserId);
              const linkTo  = isOwner ? '/mi-trastero' : `/de/${task.trastero_nombre}`;
              const precio  = task.Precio != null
                ? task.Precio.toLocaleString('es-ES') + ' €'
                : null;
              return (
                <Link key={task.id} to={linkTo} style={{ textDecoration: 'none' }}>
                  <ArticleCard>
                    <ArticleImg
                      src={`${buildBackendImgUrl(task.Ruta)}/${task.Thumb1 || task.Imagen1}`}
                      alt={task.Nombre}
                      loading="lazy"
                      onError={e => { e.target.onerror = null; e.target.src = '/placeholder.jpg'; }}
                    />
                    <ArticleInfo>
                      {precio && <ArticlePrice>{precio}</ArticlePrice>}
                      <ArticleNombre>{task.Nombre}</ArticleNombre>
                      <ArticleTrastero>{task.trastero_nombre}</ArticleTrastero>
                    </ArticleInfo>
                  </ArticleCard>
                </Link>
              );
            })}
          </PrincipalGrid>
        ) : (
          <PrincipalGrid>
            {tasks.map(task => <Cargaimg key={task.id} task={task} />)}
          </PrincipalGrid>
        )}
        {cargandoMas && (
          <div style={{ textAlign: 'center', padding: 20, color: '#666', fontSize: 14 }}>
            ⏳ Cargando más...
          </div>
        )}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </Body>
      <Suspense fallback={null}>
        {filtroAbierto && (
          <ModalFiltro
            isOpen={filtroAbierto}
            onClose={() => setFiltroAbierto(false)}
            onAplicar={aplicarFiltros}
            filtrosActivos={filtrosActivos}
          />
        )}
      </Suspense>
    </div>
  );
}

export default Principal;